import { Box, lighten, Typography, useTheme } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import type { FilterWidgetSettings } from "../../../../../api/types/dashboardsTypes";
import type { DimensionDictionary } from "../../../../../api/types/svTypes";
import type { DashboardFilter } from "../../dashboard.types";
import { toLabel } from "../../utils/withValue";

interface Props {
  filter: DashboardFilter;
  settings: FilterWidgetSettings;
  onSettingsChange: (settings: Partial<FilterWidgetSettings>) => void;
}

const TextTiles = ({ filter, settings, onSettingsChange }: Props) => {
  const selectedValues = settings.defaultValue;

  const handleSelectedValuesChange = (defaultValue: unknown[]) => {
    onSettingsChange({ defaultValue });
  };

  if (settings.singleSelect) {
    return (
      <SingleSelect
        filter={filter}
        selectedValues={selectedValues}
        onSelectedValuesChange={handleSelectedValuesChange}
      />
    );
  }

  return (
    <MultiSelect filter={filter} selectedValues={selectedValues} onSelectedValuesChange={handleSelectedValuesChange} />
  );
};

const SingleSelect = ({
  filter,
  selectedValues,
  onSelectedValuesChange,
}: {
  filter: DashboardFilter;
  selectedValues: unknown[];
  onSelectedValuesChange: (values: unknown[]) => void;
}) => {
  const handleOnChange = useCallback(
    (value: unknown, checked: boolean) => {
      if (checked) {
        onSelectedValuesChange([value]);
      }
      if (!checked) {
        onSelectedValuesChange([]);
      }
    },
    [onSelectedValuesChange]
  );

  return (
    <ScrollableFlexContainer>
      <Box
        sx={{
          display: "grid",
          flex: 1,
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1,
        }}
      >
        {filter.options.map((option) => (
          <Tile
            key={option[filter.valueField || ""]}
            filter={filter}
            option={option}
            selected={selectedValues.includes(option[filter.valueField || ""])}
            intermediate={false}
            onChange={handleOnChange}
          />
        ))}
      </Box>
    </ScrollableFlexContainer>
  );
};

const MultiSelect = ({
  filter,
  selectedValues,
  onSelectedValuesChange,
}: {
  filter: DashboardFilter;
  selectedValues: unknown[];
  onSelectedValuesChange: (values: unknown[]) => void;
}) => {
  const isSelectAll = useMemo(() => selectedValues.length === filter.options.length, [selectedValues, filter.options]);
  const isIntermediate = useMemo(
    () => selectedValues.length > 0 && selectedValues.length < filter.options.length,
    [selectedValues, filter.options]
  );

  const selectAllText = useMemo(() => {
    if (isIntermediate || isSelectAll) {
      return "Unselect All";
    }
    return "Select All";
  }, [isIntermediate, isSelectAll]);

  const handleOnChange = useCallback(
    (value: unknown, checked: boolean) => {
      if (checked) {
        onSelectedValuesChange([...selectedValues, value]);
      }
      if (!checked) {
        onSelectedValuesChange(selectedValues.filter((v) => v !== value));
      }
    },
    [onSelectedValuesChange, selectedValues]
  );

  const handleSelectAll = useCallback(() => {
    if (isIntermediate) {
      onSelectedValuesChange([]);
      return;
    }
    if (!isSelectAll) {
      onSelectedValuesChange(filter.options.map((option) => option[filter.valueField || ""]));
    }
    if (isSelectAll) {
      onSelectedValuesChange([]);
    }
  }, [filter.options, filter.valueField, isSelectAll, isIntermediate, onSelectedValuesChange]);

  return (
    <ScrollableFlexContainer>
      <Box
        sx={{
          display: "grid",
          flex: 1,
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1,
        }}
      >
        <Tile
          key={"select-all"}
          filter={filter}
          option={{ [filter.valueField || ""]: "select-all", [filter.displayField || ""]: selectAllText }}
          selected={isSelectAll}
          intermediate={isIntermediate}
          onChange={handleSelectAll}
        />
        {filter.options.map((option) => (
          <Tile
            key={option[filter.valueField || ""]}
            filter={filter}
            option={option}
            selected={selectedValues.includes(option[filter.valueField || ""])}
            intermediate={false}
            onChange={handleOnChange}
          />
        ))}
      </Box>
    </ScrollableFlexContainer>
  );
};

const Tile = memo(function Tile({
  filter,
  option,
  selected,
  intermediate,
  onChange,
}: {
  filter: DashboardFilter;
  option: DimensionDictionary;
  selected: boolean;
  intermediate: boolean;
  onChange: (value: unknown, checked: boolean) => void;
}) {
  const theme = useTheme();
  const value = option[filter.valueField || ""];
  const label = toLabel(option[filter.displayField || ""]);

  const selectedColor = useMemo(() => lighten(theme.palette.primary.main, 0.9), [theme.palette.primary.main]);

  return (
    <Box
      sx={{
        display: "flex",
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        minHeight: 42,
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        textAlign: "center",
        "&:hover": { backgroundColor: "action.hover" },
        ...(selected && { backgroundColor: selectedColor, borderColor: "primary.main" }),
        ...(intermediate && { backgroundColor: "background.paper", borderColor: "primary.main" }),
      }}
      onClick={() => onChange(value, !selected)}
    >
      <Typography variant="body1">{label}</Typography>
    </Box>
  );
});

export default TextTiles;
